import { Button } from '@ghs/components';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Box, Container, Stack, TextField, Typography } from '@mui/material';
import { useUnit } from 'effector-react';
import { useNavigate } from 'react-router-dom';
import FeedbackPanel from '../../../../components/FeedbackPanel/FeedbackPanel';
import useMountedEvent from '../../../../hooks/useMountedEvent';
import { emailRegex } from '../../../../util/JsonUtils';
import { allowedResetPasswordEmailRegex, $$resetPasswordUtils } from './model';

export default function GhsAdminResetPasswordPage() {
  useMountedEvent($$resetPasswordUtils.pageMounted);

  const navigate = useNavigate();
  const email = useUnit($$resetPasswordUtils.$resetPasswordEmail);
  const isResetPasswordPending = useUnit($$resetPasswordUtils.$isResetPasswordPending);

  const trimmedEmail = email.trim();
  const isValidEmailFormat = emailRegex.test(trimmedEmail);
  const isAllowedEmailDomain = allowedResetPasswordEmailRegex.test(trimmedEmail);
  const canSubmit = isValidEmailFormat && isAllowedEmailDomain && !isResetPasswordPending;
  let emailHelperText = ' ';
  if (email.length > 0 && !isValidEmailFormat) {
    emailHelperText = 'Please enter a valid email address';
  } else if (email.length > 0 && !isAllowedEmailDomain) {
    emailHelperText = 'Only @ghs.com or @ghstest.com addresses are allowed';
  }

  return (
    <Container id="reset-password-utility-container">
      <Stack spacing={3}>
        <Button color="primary" size="medium" variant="text" sx={{ alignSelf: 'flex-start', pl: 0 }} startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate('/ghsAdmin/utils')}>
          Back to Internal Utilities
        </Button>

        <Stack spacing={3}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: 2,
                bgcolor: 'action.hover',
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <LockOutlinedIcon />
            </Box>
            <Typography variant="h4" color="text.primary">
              Reset Password
            </Typography>
          </Stack>

          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', maxWidth: 720 }}>
            <Typography variant="h5" color="text.primary" sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
              Reset Password
            </Typography>
            <Stack
              component="form"
              spacing={2}
              sx={{ p: 3 }}
              onSubmit={event => {
                event.preventDefault();
                if (canSubmit) {
                  $$resetPasswordUtils.resetPasswordButtonClicked();
                }
              }}
            >
              <FeedbackPanel model={$$resetPasswordUtils.feedbackPanel} />
              <Typography variant="body1" color="text.secondary">
                Enter the email address of the account and click Reset Password.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Only <strong>@ghs.com</strong> and <strong>@ghstest.com</strong> email addresses are allowed at this time.
              </Typography>
              <TextField
                fullWidth
                label="* Email Address"
                value={email}
                onChange={event => $$resetPasswordUtils.setResetPasswordEmail(event.target.value)}
                error={email.length > 0 && (!isValidEmailFormat || !isAllowedEmailDomain)}
                helperText={emailHelperText}
              />
              <Stack direction="row">
                <Button type="submit" color="primary" variant="contained" disabled={!canSubmit}>
                  Reset Password
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </Stack>
    </Container>
  );
}
